import { ON_SCREEN } from './components/LiveBarcodeFeed';
import type { BarcodeScan } from './components/LiveBarcodeFeed';

const KEY_PREFIX = 'fulfillment.viewOrders.liveBarcodeFeed.';

/** The desk's own calendar day; a UTC day would roll the feed over mid-morning in Seoul. */
const dayOf = (now: Date): string =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

const isScan = (value: unknown): value is BarcodeScan => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const { code, at, worker } = value as Record<string, unknown>;
  return typeof code === 'string' && typeof at === 'string' && typeof worker === 'string';
};

/**
 * Today's scans as this browser recorded them, so a reload does not blank the feed. Nothing server-side
 * keeps the feed, so it is per workstation; the days before are dropped on the way in, which is what
 * makes the feed start empty each morning. A store that cannot be read is an empty feed, never an error.
 */
export const loadTodaysScans = (now: Date): BarcodeScan[] => {
  const today = `${KEY_PREFIX}${dayOf(now)}`;
  try {
    const stale = Array.from({ length: window.localStorage.length }, (_, index) =>
      window.localStorage.key(index),
    ).filter((key): key is string => key !== null && key.startsWith(KEY_PREFIX) && key !== today);
    for (const key of stale) {
      window.localStorage.removeItem(key);
    }
    const raw = window.localStorage.getItem(today);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(isScan).slice(0, ON_SCREEN) : [];
  } catch {
    return [];
  }
};

/** Records the feed under today's key; an unwritable store only costs the desk the feed across a reload. */
export const saveTodaysScans = (scans: BarcodeScan[], now: Date): void => {
  try {
    window.localStorage.setItem(`${KEY_PREFIX}${dayOf(now)}`, JSON.stringify(scans.slice(0, ON_SCREEN)));
  } catch {
    // Nothing to do: the feed on screen is unaffected.
  }
};

/** Today's key, so the screen can tell a feed carried over from yesterday from one started today. */
export const feedDayOf = dayOf;
